'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@crabs-ai/ds-react';
import {
  TransitionDragData,
  TransitionType,
  TRANSITION_DRAG_TYPE,
  setCurrentTransitionDragData,
} from './transition-drag-data';
import TransitionPreview from './TransitionPreview';

interface TransitionItemProps {
  type: TransitionType;
  name: string;
}

const PREVIEW_DURATION = 30; // 프리뷰 프레임 수
const FRAME_RATE = 30; // fps

export default function TransitionItem({ type, name }: TransitionItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const animationRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  // hover 시 애니메이션 시작
  useEffect(() => {
    if (isHovered && !isDragging) {
      startTimeRef.current = null;

      const animate = (timestamp: number) => {
        if (!startTimeRef.current) {
          startTimeRef.current = timestamp;
        }

        const elapsed = timestamp - startTimeRef.current;
        const duration = (PREVIEW_DURATION / FRAME_RATE) * 1000; // ms
        const currentProgress = Math.min(elapsed / duration, 1);

        setProgress(currentProgress);

        // 애니메이션이 끝나면 멈춤 (반복하지 않음)
        if (currentProgress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        }
      };

      animationRef.current = requestAnimationFrame(animate);

      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    } else {
      // hover 종료 시 초기 상태로 리셋
      setProgress(0);
      startTimeRef.current = null;
    }
  }, [isHovered, isDragging]);

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  // 드래그 시작 핸들러
  const handleDragStart = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      setCurrentTransitionDragData(null);

      const dragData: TransitionDragData = {
        type,
        name,
      };

      e.dataTransfer.setData(TRANSITION_DRAG_TYPE, JSON.stringify(dragData));
      e.dataTransfer.effectAllowed = 'copy';
      setIsDragging(true);

      // 전역 드래그 데이터 설정 (dragover에서 사용)
      setCurrentTransitionDragData(dragData);

      // 투명한 드래그 이미지 설정 (커스텀 오버레이 사용)
      const transparentImage = document.createElement('div');
      transparentImage.style.cssText = `
        position: absolute;
        top: -9999px;
        left: -9999px;
        width: 1px;
        height: 1px;
        opacity: 0;
      `;
      document.body.appendChild(transparentImage);
      e.dataTransfer.setDragImage(transparentImage, 0, 0);

      // 드래그 이미지 설정 후 DOM에서 제거 (약간의 딜레이 필요)
      requestAnimationFrame(() => {
        document.body.removeChild(transparentImage);
      });
    },
    [type, name],
  );

  // 드래그 종료 핸들러
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    setCurrentTransitionDragData(null);
  }, []);

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        'flex cursor-grab flex-col p-4xs',
        isDragging && 'opacity-50',
      )}
    >
      <div
        className={cn(
          'aspect-[16/9] w-full overflow-hidden rounded-xs transition-all',
          isHovered
            ? 'border-sys-style-primary-default'
            : 'border-sys-style-neutral-default',
        )}
      >
        <TransitionPreview
          type={type}
          progress={progress}
        />
      </div>
      <span className="p-2xs text-title-small text-sys-cont-neutral-default">
        {name}
      </span>
    </div>
  );
}
